import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, APIResponse, refreshSession, getSession, ACCOUNTANT_API_URL } from '../../../../../../../app/Api';
import { formatDate, timestampToDate } from '../../../../../../../app/Helpers';
import { DynamicField } from '../../../../../../common/DynamicFields/DynamicFields';
import { AccountAuthorizationEndpointClient } from '../../../../../../../repository/Accountant/Account_autorizationServiceClientPb';
import { GetAccountAuthorizationResponse, GetAccountAuthorizationRequest, DeleteAccountAuthorizationRequest, DeleteAccountAuthorizationResponse } from '../../../../../../../repository/Accountant/account_autorization_pb';
import { GetAccountRequest, GetAccountResponse } from '../../../../../../../repository/Accountant/account_pb';
import { AccountEndpointClient } from '../../../../../../../repository/Accountant/AccountServiceClientPb';
import { AuthorizationEndpointClient } from '../../../../../../../repository/Accountant/AuthorizationServiceClientPb';
import { GetAuthorizationRequest, RevokeAuthorizationRequest, RevokeAuthorizationResponse, GetAuthorizationResponse } from '../../../../../../../repository/Accountant/authorization_pb';
import { AuthorizationStatus } from '../../../../../../../app/Enums';


export interface AuthorizationDetailsPageState {
    item: any,
    account: any,
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    isLoading: boolean,
    accountLoading: boolean,

}

const initialState: AuthorizationDetailsPageState = {
    item: undefined,
    isChangeStateLoading: false,
    message: undefined,
    isLoading: false,
    accountLoading: false,

    account: undefined

}

const client = new AuthorizationEndpointClient(ACCOUNTANT_API_URL, null, null);
const accountClient = new AccountEndpointClient(ACCOUNTANT_API_URL, null, null);


export const getAccount = createAsyncThunk<GetAccountResponse.AsObject, APIRequest<GetAccountRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/accountant/accountDetails/authorizationDetailsPage/getAccount',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await accountClient.getAccount(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)



export const get = createAsyncThunk<GetAuthorizationResponse.AsObject, APIRequest<GetAuthorizationRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/accountant/accountDetails/authorizationDetailsPage/get',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await client.getAuthorization(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)



export const revokeAutorization = createAsyncThunk<APIResponse<RevokeAuthorizationResponse.AsObject>, APIRequest<RevokeAuthorizationRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/accountant/accountDetails/authorizationDetailsPage/revoke',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.revoke(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)


export const authorizationDetailsPageSlice = createSlice({
    name: 'pages/accountant/accountDetails/authorizationDetailsPage',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
            state.account = initialState.account
            state.accountLoading = initialState.accountLoading;

        },

        resetItem: (state) => {
            state.item = undefined;

        },

        updateItem: (state, action: PayloadAction<any>) => {
            state.item = action.payload;

        }
    },
    extraReducers: (builder) => {
        builder.addCase(revokeAutorization.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
                state.item.status = AuthorizationStatus.REVOKED
            }
        })
        builder.addCase(revokeAutorization.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(revokeAutorization.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })

        builder.addCase(get.fulfilled, (state, { payload }) => {
            state.isLoading = false
            if (payload) {

                let r = {
                    id: payload.success?.id,
                    accountId: payload.success?.account?.id?.value,
                    accountName: payload.success?.account?.name?.value ?? "",
                    token: payload.success?.token?.value ?? "",
                    accountCurrencyId: payload.success?.account?.currency?.id?.value ?? "",
                    accountCurrencyName: payload.success?.account?.currency?.name?.value ?? "",
                    accountCurrencyCode: payload.success?.account?.currency?.code?.value ?? "",
                    accountCurrencyDecimalPlaces: payload.success?.account?.currency?.decimalplaces?.value ?? undefined,
                    accountCurrencyForm: payload.success?.account?.currency?.form?.value ?? undefined,
                    accountCurrencySymbol: payload.success?.account?.currency?.symbol?.value ?? "",
                    expirationDate: formatDate(timestampToDate(payload.success?.expirationdate?.seconds, payload.success?.expirationdate?.nanos)),
                    isRequest: payload.success?.isrequest?.value,
                    status: payload.success?.status?.id?.value,
                    operation: payload.success?.operation?.value,
                    extraFields: [] as DynamicField[],
                    createdDate: formatDate(timestampToDate(payload.success?.createddate?.seconds, payload.success?.createddate?.nanos)),
                    addedBy: (payload.success?.createdby?.firstname?.value ?? "") + " " + (payload.success?.createdby?.lastname?.value ?? ""),
                    addedById: payload.success?.createdby?.id?.value,
                    params: [] as { key: string | undefined, value: string | undefined }[],

                }

                if (payload.success?.parametersList) {
                    payload.success?.parametersList?.forEach(e => {
                        r.params.push({ key: e.key, value: e.value })
                    })
                }

                if (payload?.success?.extrafieldsList) {
                    let count = 0
                    payload?.success?.extrafieldsList?.forEach(element => {
                        r.extraFields.push({ key: element.key, label: element.label, type: element.type, value: element.value })
                        count++
                    });
                }


                state.item = r
            }
        })
        builder.addCase(get.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(get.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })

        builder.addCase(getAccount.fulfilled, (state, { payload }) => {
            state.accountLoading = false
            if (payload) {
                let r = {
                    id: payload.success?.id,
                    internalId: payload.success?.internalid,
                    name: payload.success?.name?.value ?? "",
                    code: payload.success?.code?.value ?? "",
                    currencyId: payload.success?.currency?.id?.value ?? "",
                    currencyName: payload.success?.currency?.name?.value ?? "",
                    currencyCode: payload.success?.currency?.code?.value ?? "",
                    currencyDecimalPlaces: payload.success?.currency?.decimalplaces?.value ?? undefined,
                    currencyForm: payload.success?.currency?.form?.value ?? undefined,
                    currencySymbol: payload.success?.currency?.symbol?.value ?? "",
                    accountTypeId: payload.success?.accounttype?.id?.value ?? "",
                    accountTypeName: payload.success?.accounttype?.name?.value ?? "",
                    status: payload.success?.status?.value ?? undefined,
                    ownerFirstname: payload.success?.owner?.firstname?.value ?? "",
                    ownerLastname: payload.success?.owner?.lastname?.value ?? "",
                    ownerMiddlename: payload.success?.owner?.middlename?.value ?? "",
                    ownerName: (payload.success?.owner?.firstname?.value ?? "") + " " + (payload.success?.owner?.lastname?.value ?? ""),
                    ownerId: payload.success?.owner?.id?.value ?? "",
                    createdDate: formatDate(timestampToDate(payload.success?.createddate?.seconds, payload.success?.createddate?.nanos)),
                    addedBy: (payload.success?.createdby?.firstname?.value ?? "") + " " + (payload.success?.createdby?.lastname?.value ?? ""),
                    addedById: payload.success?.createdby?.id?.value,
                    limitationId: payload.success?.limitation?.id?.value ?? "",
                    limitationName: payload.success?.limitation?.name?.value ?? "",
                    freeBalance: undefined,
                    reservedBalance: undefined,
                    totalBalance: undefined,
                    extraFields: [] as DynamicField[],
                    isAuthorizationRequired: payload.success?.accountsetting?.isauthorizationrequired,
                    isLinkingEnable: payload.success?.accountsetting?.islinkingenable?.value,
                    isLinkingMetadataSet: payload.success?.accountsetting?.islinkingmetadataset?.value,
                    transferControl: payload.success?.accountsetting?.tansfercontrol?.value,



                }

                if (payload?.success?.extrafieldsList) {
                    let count = 0
                    payload?.success?.extrafieldsList?.forEach(element => {
                        r.extraFields.push({ key: element.key, label: element.label, type: element.type, value: element.value })
                        count++
                    });
                }

                state.account = r

            }
        })
        builder.addCase(getAccount.rejected, (state, action) => {
            state.accountLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(getAccount.pending, (state, action) => {
            state.accountLoading = true;
            state.message = undefined;
        })
    }
})

export const { reset, dismissMessage, resetItem, updateItem } = authorizationDetailsPageSlice.actions

export default authorizationDetailsPageSlice.reducer